/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useMemo } from 'react';

// Mantine
import { Carousel } from '@mantine/carousel';
import {
	Box,
	Card,
	Flex,
	Group,
	Image,
	Skeleton,
	Stack,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

// Styles
import classes from './NewsSection.module.css';

// UI local components
import {
	Button,
	CardNews,
	Text,
	Title,
} from '@components/v2/ui';

// I18n
import { useTranslation } from 'react-i18next';

// Assets
import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';

// Lib
import { useAuthorization } from '@lib/authorization';

// Constants
import { NOT_APPLICABLE } from '@config/constants/common';
import { ROLES } from '@config/constants/roles';
import { TPost } from '@global-types/api/api';

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

type TNewsSectionProps = {
	onSeeMoreClick?: () => void;
	posts: TPost[];
	isPendingGetPosts: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                  Skeleton                                  */
/* -------------------------------------------------------------------------- */

export const NewsCardSkeleton = () => (
	<Flex flex={1}>
		<Card p="16px" radius="md" w="100%" miw="293px">
			<Skeleton h={211} radius="md" />
			<Stack mt="15px" gap="xs">
				<Group gap="xs">
					<Skeleton circle height={20} width={20} />
					<Skeleton height={10} width="40%" />
				</Group>
				<Skeleton height={14} width="60%" />
				<Skeleton height={10} width="100%" />
				<Skeleton height={10} width="90%" />
			</Stack>
		</Card>
	</Flex>
);

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export const NewsSection = ({
	onSeeMoreClick,
	posts,
	isPendingGetPosts,
}: TNewsSectionProps) => {
	/* ***************************** HOOKS ***************************** */

	const { t } = useTranslation();
	const { checkAccess } = useAuthorization();

	/* ***************************** HELPERS ***************************** */
	const formatAuthor = (
		firstName?: string,
		lastName?: string
	): string => {
		const fullName =
			`${firstName ?? ''} ${lastName ?? ''}`.trim();
		return fullName !== '' ? fullName : NOT_APPLICABLE;
	};

	/* *************************** RESPONSIVE **************************** */
	const isBase = useMediaQuery('(max-width: 36em)');
	const isSm = useMediaQuery(
		'(min-width: 36em) and (max-width: 62em)'
	);
	const isMd = useMediaQuery('(min-width: 62em)');

	const visibleSlides = useMemo(() => {
		if (isBase) return 1;
		if (isSm) return 2;
		if (isMd) return 4;
		return 1;
	}, [isBase, isSm, isMd]);

	const showControls =
		posts &&
		Array.isArray(posts) &&
		posts.length > visibleSlides;

	/* ***************************** RENDER ****************************** */
	return (
		<Box className={classes.containerLastNews}>
			<Box className={classes.header}>
				<Title
					c="var(--mantine-color-igluPrimary-9)"
					order={6}
				>
					{t('global.lastNews')}
				</Title>
				<Button
					variant="outline"
					onClick={onSeeMoreClick}
				>
					{t('global.viewMore')}
				</Button>
			</Box>

			<Carousel
				withIndicators={false}
				withControls={showControls}
				slideSize={{ base: '100%', sm: '50%', md: '25%' }}
				emblaOptions={{ loop: true, align: 'start' }}
				slideGap="8px"
				p="0px"
			>
				{isPendingGetPosts ? (
					Array.from({ length: 5 }).map((_, i) => (
						<Carousel.Slide key={i}>
							<NewsCardSkeleton />
						</Carousel.Slide>
					))
				) : posts && posts.length > 0 ? (
					posts.map((post) => (
						<Carousel.Slide key={post.id}>
							<div style={{ margin: '10px' }}>
								<CardNews
									title={post.title ?? NOT_APPLICABLE}
									author={formatAuthor(
										post?.createdBy?.firstName,
										post?.createdBy?.lastName
									)}
									description={
										post?.content ?? NOT_APPLICABLE
									}
									image={post?.cover}
									onClick={onSeeMoreClick}
									showAuthor={checkAccess({
										allowedRoles: [
											ROLES.ADMIN,
											ROLES.SUPERADMIN,
											ROLES.STAFF,
										],
									})}
								/>
							</div>
						</Carousel.Slide>
					))
				) : (
					<Flex
						direction="column"
						justify="center"
						align="center"
						flex={1}
					>
						<Image
							src={NoRecordsIcon}
							className={classes.iconNoRecords}
						/>
						<Text
							variant="caption"
							c="var(--mantine-color-igluTextSecondary-9)"
						>
							{t('global.datatable.noResultsFound')}
						</Text>
					</Flex>
				)}
			</Carousel>
		</Box>
	);
};
